// src/pages/UserDashboardPage.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DashboardCard from "../components/DashboardCard";
import { getUserDashboard } from "../services/DashboardService";
import {
  ChartBarIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  LanguageIcon,
  ClockIcon,
  ArrowRightIcon,
  TrendingUpIcon,
  StarIcon,
  CalendarIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "../components/Icons"; // Assuming these are accessible

const UserDashboardPage = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mainContentRef = useRef(null);
  const errorDisplayRef = useRef(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getUserDashboard();
        setDashboardData(data);
        if (mainContentRef.current) {
          mainContentRef.current.focus();
        }
      } catch (err) {
        setError("Failed to load dashboard data. Please try again later.");
        console.error("Dashboard data fetch error:", err);
         if (errorDisplayRef.current) {
          errorDisplayRef.current.focus();
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      console.error("Error formatting date:", dateString, e);
      return "Date Error";
    }
  };

  const getActivityStatus = (type) => {
    const statusMap = {
      EnglishPractice: { color: "text-emerald-300", bg: "bg-emerald-700/30" }, // Adjusted contrast
      Interview: { color: "text-sky-300", bg: "bg-sky-700/30" }, // Adjusted contrast (blue -> sky)
      Assessment: { color: "text-purple-300", bg: "bg-purple-700/30" }, // Adjusted contrast
    };
    return statusMap[type] || { color: "text-slate-300", bg: "bg-slate-700/30" }; // Adjusted contrast
  };

  const getProficiencyColor = (level) => {
    const colors = {
      Beginner: "text-orange-300", // Adjusted contrast
      Intermediate: "text-yellow-300", // Adjusted contrast
      Advanced: "text-green-300", // Adjusted contrast
      Expert: "text-sky-300", // Adjusted contrast (blue -> sky)
    };
    return colors[level] || "text-slate-300"; // Adjusted contrast
  };

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div aria-hidden="true" className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500 mx-auto mb-4"></div> {/* Contrast Fix */}
          <p className="text-slate-100 text-lg">Loading your dashboard...</p> {/* Contrast Fix */}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <main role="alert" aria-live="assertive" ref={errorDisplayRef} tabIndex={-1} className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center outline-none">
        <div className="text-center p-8 bg-slate-800/50 rounded-lg shadow-xl max-w-md mx-auto">
          <ExclamationTriangleIcon aria-hidden="true" className="w-12 h-12 text-red-400 mx-auto mb-4" /> {/* Contrast Fix */}
          <p className="text-slate-100 text-xl font-semibold mb-2"> {/* Contrast Fix */}
            Oops! Something went wrong.
          </p>
          <p className="text-red-300 text-sm">{error}</p> {/* Contrast Fix */}
           <button
            onClick={() => window.location.reload()} // Simple retry
            className="mt-6 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white font-semibold rounded-lg"
          >
            Try Again
          </button>
        </div>
      </main>
    );
  }

  if (!dashboardData) {
    return (
      <main role="region" aria-live="polite" ref={mainContentRef} tabIndex={-1} className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center outline-none">
        <div className="text-center p-8 bg-slate-800/50 rounded-lg shadow-xl max-w-md mx-auto">
          <DocumentTextIcon aria-hidden="true" className="w-12 h-12 text-sky-400 mx-auto mb-4" /> {/* Contrast Fix */}
          <p className="text-slate-100 text-xl font-semibold mb-2"> {/* Contrast Fix */}
            No Dashboard Data
          </p>
          <p className="text-sky-200 text-sm"> {/* Contrast Fix */}
            We couldn't find any dashboard data for your account at the moment.
          </p>
        </div>
      </main>
    );
  }

  const activityStatus = getActivityStatus(dashboardData.lastActivityType);

  let latestEnglishProficiency = {
    estimatedProficiencyLevel: "N/A",
    strengths: [],
    areasForImprovement: [],
    topic: "General Conversation",
  };

  if (
    dashboardData.latestEnglishSessionProficiency &&
    typeof dashboardData.latestEnglishSessionProficiency === "string"
  ) {
    try {
      const parsedProficiency = JSON.parse(
        dashboardData.latestEnglishSessionProficiency
      );
      if (parsedProficiency && typeof parsedProficiency === "object") {
        latestEnglishProficiency = {
          estimatedProficiencyLevel:
            parsedProficiency.estimatedProficiencyLevel ?? "N/A",
          strengths: Array.isArray(parsedProficiency.strengths)
            ? parsedProficiency.strengths
            : [],
          areasForImprovement: Array.isArray(
            parsedProficiency.areasForImprovement
          )
            ? parsedProficiency.areasForImprovement
            : [],
          topic: parsedProficiency.topic ?? "General Conversation",
        };
      } else {
        console.warn(
          "Parsed latestEnglishSessionProficiency was null or not a valid object:",
          parsedProficiency
        );
      }
    } catch (e) {
      console.error(
        "Failed to parse latestEnglishSessionProficiency:",
        dashboardData.latestEnglishSessionProficiency,
        e
      );
    }
  }

  const latestInterviewFeedbackFull =
    dashboardData.latestInterviewFeedback ?? "";
  const latestInterviewFeedbackLines = latestInterviewFeedbackFull.split("\n");
  const latestInterviewFeedbackSummary =
    latestInterviewFeedbackLines[0] || "No feedback details available.";
  const latestInterviewFeedbackHasMore =
    latestInterviewFeedbackLines.length > 1 &&
    latestInterviewFeedbackLines[1].trim() !== "";

  return (
    <main ref={mainContentRef} tabIndex={-1} className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-slate-100 outline-none"> {/* Base text color, focus target */}
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
        <div className="relative px-4 sm:px-8 pt-8 pb-12">
          <header className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border-4 border-white/20 shadow-2xl">
                  <UserIcon aria-hidden="true" className="w-10 h-10 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-white to-sky-200 bg-clip-text text-transparent"> {/* Contrast Fix for blue-200 */}
                  Welcome back,{" "}
                  {dashboardData.firstName ??
                    (dashboardData.fullName || "User")}
                  !
                </h1>
                <p className="text-xl text-sky-200 mt-2"> {/* Contrast Fix */}
                  Continue your journey to excellence
                </p>
              </div>
            </div>
          </header>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 pb-12">
        {/* Quick Stats */}
        <section aria-labelledby="quick-stats-heading" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 -mt-6 mb-12">
          <h2 id="quick-stats-heading" className="sr-only">Quick Statistics</h2>
          <DashboardCard className="bg-gradient-to-br from-sky-700/30 to-sky-900/30 border-sky-600/40"> {/* Adjusted contrast */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-sky-200 mb-1">Questions Answered</p> {/* Contrast Fix */}
                <p className="text-3xl font-bold text-sky-300"> {/* Contrast Fix */}
                  {dashboardData.totalInterviewQuestionsAnswered ?? 0}
                </p>
              </div>
              <DocumentTextIcon aria-hidden="true" className="w-8 h-8 text-sky-400" /> {/* Contrast Fix */}
            </div>
          </DashboardCard>

          <DashboardCard className="bg-gradient-to-br from-emerald-700/30 to-emerald-900/30 border-emerald-600/40"> {/* Adjusted contrast */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-200 mb-1"> {/* Contrast Fix */}
                  English Sessions
                </p>
                <p className="text-3xl font-bold text-emerald-300"> {/* Contrast Fix */}
                  {dashboardData.totalEnglishSessions ?? 0}
                </p>
              </div>
              <LanguageIcon aria-hidden="true" className="w-8 h-8 text-emerald-400" /> {/* Contrast Fix */}
            </div>
          </DashboardCard>

          <DashboardCard className="bg-gradient-to-br from-purple-700/30 to-purple-900/30 border-purple-600/40"> {/* Adjusted contrast */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-200 mb-1">Current Level</p> {/* Contrast Fix */}
                <p
                  className={`text-lg font-semibold ${getProficiencyColor(
                    latestEnglishProficiency.estimatedProficiencyLevel
                  )}`}
                >
                  {latestEnglishProficiency.estimatedProficiencyLevel}
                </p>
              </div>
              <TrendingUpIcon aria-hidden="true" className="w-8 h-8 text-purple-400" /> {/* Contrast Fix */}
            </div>
          </DashboardCard>

          <DashboardCard
            className={`bg-gradient-to-br ${activityStatus.bg} border-opacity-30`} // activityStatus colors updated
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-200 mb-1">Last Activity</p> {/* Contrast Fix */}
                <p className={`text-lg font-semibold ${activityStatus.color}`}>
                  {dashboardData.lastActivityType ?? "None"}
                </p>
              </div>
              <ClockIcon aria-hidden="true" className={`w-8 h-8 ${activityStatus.color}`} />
            </div>
          </DashboardCard>
        </section>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Interview Preparation Section */}
          <section aria-labelledby="interview-prep-heading" className="xl:col-span-2 flex flex-col"> {/* Added flex flex-col for h-full on card */}
            <DashboardCard
              title="Interview Preparation"
              titleAs="h2" // This is the main heading for this section card
              id="interview-prep-heading"
              className="h-full flex flex-col bg-gradient-to-br from-slate-800/60 to-slate-900/70 border-slate-700/60" // Darker, more opaque for content
            >
              <div className="flex items-center mb-6">
                <AcademicCapIcon aria-hidden="true" className="w-7 h-7 text-sky-400 mr-3 flex-shrink-0" />
                <h3 className="text-xl font-semibold text-slate-100">
                  Technical & Behavioral Skills
                </h3>
              </div>

              <p className="text-slate-300 mb-6 text-sm leading-relaxed flex-grow">
                {dashboardData.interviewPreparationDescription ??
                  "Focus on practicing common technical questions and refining your STAR method responses for behavioral questions. Consistent practice is key!"}
              </p>

              {dashboardData.latestInterviewDate ? (
                <div className="mb-6 p-4 bg-slate-700/40 rounded-xl border border-slate-600/40"> {/* Slightly lighter for nesting */}
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-slate-100 flex items-center">
                      <CalendarIcon aria-hidden="true" className="w-5 h-5 mr-2 text-slate-400 flex-shrink-0" />
                      Latest Interview
                    </h4>
                    <span className="text-sm text-slate-400">
                      {formatDate(dashboardData.latestInterviewDate)}
                    </span>
                  </div>

                  <div className="bg-slate-800/60 rounded-lg p-3 mb-3"> {/* Darker for contrast */}
                    <p className="text-sm text-slate-200 leading-relaxed line-clamp-3"> {/* line-clamp for controlled summary */}
                      {latestInterviewFeedbackSummary}
                      {latestInterviewFeedbackHasMore && "..."}
                    </p>
                  </div>

                  <button
                    onClick={() => navigate("/dashboard/interview-history")}
                    className="text-sm text-sky-400 hover:text-sky-300 flex items-center transition-colors group"
                  >
                    View Full Feedback
                    <ArrowRightIcon aria-hidden="true" className="w-4 h-4 ml-1 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </button>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-slate-700/40 rounded-xl border border-slate-600/40 text-center">
                  <CalendarIcon aria-hidden="true" className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">
                    No recent interview data.
                  </p>
                  <p className="text-slate-500 text-xs">
                    Start an interview to see your feedback here.
                  </p>
                </div>
              )}

              <div className="mt-auto flex flex-col sm:flex-row gap-4 pt-4"> {/* mt-auto to push to bottom, pt-4 for spacing */}
                <button
                  onClick={() => navigate("/interview")}
                  className="flex-1 px-6 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-sky-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-blue-500/30 transform hover:scale-105 transition-all duration-300 flex items-center justify-center text-base" /* Adjusted padding & text size */
                >
                  <DocumentTextIcon aria-hidden="true" className="w-5 h-5 mr-2" />
                  Start New Interview
                </button>
                <button
                  onClick={() => navigate("/dashboard/interview-history")}
                  className="flex-1 px-6 py-3 sm:py-4 bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold rounded-xl border border-slate-600 transition-all duration-300 flex items-center justify-center text-base" /* Adjusted padding & text size */
                >
                  <ChartBarIcon aria-hidden="true" className="w-5 h-5 mr-2" />
                  View History
                </button>
              </div>
            </DashboardCard>
          </section>

          {/* English Practice Section */}
          <section aria-labelledby="english-practice-heading" className="flex flex-col"> {/* Added flex flex-col for h-full on card */}
            <DashboardCard
              title="English Speaking Practice"
              titleAs="h2" // This is the main heading for this section card
              id="english-practice-heading"
              className="h-full flex flex-col bg-gradient-to-br from-emerald-800/40 to-emerald-900/50 border-emerald-700/40" // Darker, more opaque
            >
              <div className="flex items-center mb-6">
                <LanguageIcon aria-hidden="true" className="w-7 h-7 text-emerald-300 mr-3 flex-shrink-0" />
                <h3 className="text-lg font-semibold text-slate-100">
                  Fluency & Confidence
                </h3>
              </div>

              <p className="text-slate-300 mb-6 text-sm leading-relaxed flex-grow">
                {dashboardData.englishSpeakingPracticeDescription ??
                  "Regular practice helps improve pronunciation, vocabulary, and overall conversational skills. Aim for daily short sessions."}
              </p>

              {dashboardData.latestEnglishSessionDate ? (
                <div className="mb-6 space-y-4">
                  <div className="p-4 bg-emerald-900/30 rounded-xl border border-emerald-700/40"> {/* Slightly lighter */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-emerald-100"> {/* Brighter text */}
                        Latest Session ({latestEnglishProficiency.topic})
                      </span>
                      <span className="text-xs text-slate-400">
                        {formatDate(dashboardData.latestEnglishSessionDate)}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-slate-200 mr-2">Level:</span> {/* Brighter text */}
                      <span
                        className={`text-sm font-semibold ${getProficiencyColor(
                          latestEnglishProficiency.estimatedProficiencyLevel
                        )}`}
                      >
                        {latestEnglishProficiency.estimatedProficiencyLevel}
                      </span>
                    </div>
                  </div>

                  {(latestEnglishProficiency.strengths.length > 0 ||
                    latestEnglishProficiency.areasForImprovement.length >
                      0) && (
                    <div className="grid grid-cols-1 gap-3">
                      {latestEnglishProficiency.strengths.length > 0 && (
                        <div className="p-3 bg-green-900/30 rounded-lg border border-green-700/40"> {/* Slightly lighter */}
                          <div className="flex items-center mb-2">
                            <CheckCircleIcon aria-hidden="true" className="w-5 h-5 text-green-300 mr-2 flex-shrink-0" />
                            <span className="text-sm font-medium text-green-100"> {/* Brighter text */}
                              Strengths
                            </span>
                          </div>
                          <ul className="text-xs text-slate-300 space-y-1 list-disc list-inside pl-1">
                            {latestEnglishProficiency.strengths.map(
                              (strength, index) => (
                                <li key={index}>{strength}</li>
                              )
                            )}
                          </ul>
                        </div>
                      )}

                      {latestEnglishProficiency.areasForImprovement.length >
                        0 && (
                        <div className="p-3 bg-orange-900/30 rounded-lg border border-orange-700/40"> {/* Slightly lighter */}
                          <div className="flex items-center mb-2">
                            <TrendingUpIcon aria-hidden="true" className="w-5 h-5 text-orange-300 mr-2 flex-shrink-0" />
                            <span className="text-sm font-medium text-orange-100"> {/* Brighter text */}
                              Focus Areas
                            </span>
                          </div>
                          <ul className="text-xs text-slate-300 space-y-1 list-disc list-inside pl-1">
                            {latestEnglishProficiency.areasForImprovement.map(
                              (area, index) => (
                                <li key={index}>{area}</li>
                              )
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-6 p-4 bg-emerald-900/30 rounded-xl border border-emerald-700/40 text-center"> {/* Slightly lighter */}
                  <LanguageIcon aria-hidden="true" className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">
                    No recent English session data.
                  </p>
                  <p className="text-slate-500 text-xs">
                    Start a practice session to track your progress.
                  </p>
                </div>
              )}

              <div className="mt-auto space-y-3 pt-4"> {/* mt-auto to push to bottom */}
                <button
                  onClick={() => navigate("/english-practice")}
                  className="w-full px-6 py-3 sm:py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-emerald-500/30 transform hover:scale-105 transition-all duration-300 flex items-center justify-center text-base" /* Adjusted padding & text size */
                >
                  <LanguageIcon aria-hidden="true" className="w-5 h-5 mr-2" />
                  Continue Practice
                </button>
                <button
                  onClick={() => navigate("/dashboard/english-history")}
                  className="w-full px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold rounded-xl border border-slate-600 transition-all duration-300 flex items-center justify-center text-base" /* Adjusted padding & text size */
                >
                  <ChartBarIcon aria-hidden="true" className="w-5 h-5 mr-2" />
                  View Progress
                </button>
              </div>
            </DashboardCard>
          </section>
        </div>

        {/* Quick Actions */}
        <section aria-labelledby="quick-actions-heading" className="mt-12">
          <h2 id="quick-actions-heading" className="text-2xl font-bold text-slate-100 mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <button
              onClick={() => navigate("/english-practice")}
              className="p-6 bg-gradient-to-br from-purple-700/40 to-purple-900/50 border border-purple-600/50 rounded-xl hover:scale-105 transition-all duration-300 text-left focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-slate-900" /* Darker, more opaque backgrounds */
            >
              <StarIcon aria-hidden="true" className="w-8 h-8 text-purple-300 mb-3" />
              <h3 className="text-lg font-semibold text-slate-100 mb-2">
                Take Assessment
              </h3>
              <p className="text-sm text-slate-300">
                Evaluate your current skill level
              </p>
            </button>

            <button
              onClick={() => alert("Coming soon!")}
              className="p-6 bg-gradient-to-br from-amber-700/40 to-amber-900/50 border border-amber-600/50 rounded-xl hover:scale-105 transition-all duration-300 text-left focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-slate-900" /* Darker, more opaque backgrounds */
            >
              <UserIcon aria-hidden="true" className="w-8 h-8 text-amber-300 mb-3" />
              <h3 className="text-lg font-semibold text-slate-100 mb-2">
                Update Profile
              </h3>
              <p className="text-sm text-slate-300">
                Manage your account settings
              </p>
            </button>

            <button
              onClick={() => alert("Coming soon!")}
              className="p-6 bg-gradient-to-br from-cyan-700/40 to-cyan-900/50 border border-cyan-600/50 rounded-xl hover:scale-105 transition-all duration-300 text-left focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900" /* Darker, more opaque backgrounds */
            >
              <AcademicCapIcon aria-hidden="true" className="w-8 h-8 text-cyan-300 mb-3" />
              <h3 className="text-lg font-semibold text-slate-100 mb-2">
                Learning Resources
              </h3>
              <p className="text-sm text-slate-300">
                Access study materials and guides
              </p>
            </button>
          </div>
        </section>
      </div>
    </main>
  );
};

export default UserDashboardPage;
